'use client'

import { Animation, Cell, EmptyState, Spacer, Text } from '@vinted/web-ui'
import { isNil, omitBy } from 'lodash'
import { ReactNode, useCallback, useEffect, useState } from 'react'

import { useBreakpoint } from '@marketplace-web/breakpoints/breakpoints-feature'
import { useBrowserNavigation } from '@marketplace-web/browser/browser-navigation-util'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useAsset } from '@marketplace-web/shared/assets'
import { urlWithParams } from '@marketplace-web/shared/utils'
import { ProductItemModel } from '@marketplace-web/item-box/product-item-data'
import {
  ElectronicsVerificationBundleInfoBanner,
  ItemVerificationBundleInfoBanner,
} from '@marketplace-web/escrow-pricing/escrow-pricing-feature'

import { OurPlatformDisclaimer } from '@marketplace-web/user-agreements/legal-block-feature'
import {
  BundlingLimitationsErrorType,
  cannotBundleButtonViewEvent,
  clickEvent,
  clickListItemEvent,
  exceedingLimitItemClickEvent,
  impressionEvent,
  ReasonForNotBundling,
} from '@marketplace-web/bundles/items-bundle-data'
import { CurrencyAmountModel } from '@marketplace-web/currency/currency-data'
import { ResponseError } from '@marketplace-web/core-api/api-client-util'

import ItemsMultiselect from '../../components/ItemsMultiselect'
import BundleFooterWrapper from '../../components/BundleFooterWrapper'
import { BUNDLES_POLICY_URL, CREATE_BUNDLE_URL, MEMBER_PROFILE_URL } from '../../constants/routes'
import { useBundleSummary } from '../../hooks'
import BundlingLimitationsModal from '../../components/BundlingLimitationsModal'

import BundleHeaderWithNavigation from './BundleHeaderWithNavigation'
import useUserBundleItems from './hooks/useUserBundleItems'
import {
  getBundleTrackingIdUrlParam,
  getIsBundlingAllowed,
  removeBundleTrackingIdUrlParam,
} from './utils'
import { resolveBundleError } from './resolveBundleError'

type Props = {
  isDigitalRepublicLawEnabled: boolean
  preselectedItemIds: Array<number>
  userId: number
  transactionId: number | null
  homepageSessionId: string | null
  checkoutId: string | null
}

const BundleItems = ({
  isDigitalRepublicLawEnabled,
  preselectedItemIds,
  userId: sellerId,
  transactionId,
  homepageSessionId,
  checkoutId,
}: Props) => {
  const [isSelectedItemsUpdatedFromUrl, setIsSelectedItemsUpdatedFromUrl] = useState(false)
  const [bundleTrackingId, setBundleTrackingId] = useState<string | null>(null)
  const [lastSelectedItemId, setLastSelectedItemId] = useState<string | null>(null)
  const [selectedItems, setSelectedItems] = useState<Array<ProductItemModel>>([])
  const [isBundleReviewLoading, setIsBundleReviewLoading] = useState<boolean>(false)
  const [itemLimitationError, setItemLimitationError] = useState<{
    limit: CurrencyAmountModel | null
    type: BundlingLimitationsErrorType
  } | null>(null)
  const { track } = useTracking()
  const breakpoints = useBreakpoint()
  const translate = useTranslate('bundle_items')
  const { relativeUrl, replaceHistoryState, urlQuery, searchParams } = useBrowserNavigation()
  const asset = useAsset('/assets/animations')

  const { bundleSummary, isBundleSummaryLoading, bundleError, escrowNoteTranslationKey } =
    useBundleSummary({
      selectedItemIds: selectedItems.map(item => item.id),
      sellerId,
      isEnabled: isSelectedItemsUpdatedFromUrl,
      isItemAdded: !!lastSelectedItemId,
    })

  const {
    items: bundleItems,
    isLoading,
    fetchNextPage,
    endReached,
    offlineVerificationFee,
    containsOfflineVerificationItem,
  } = useUserBundleItems({
    preselectedItemIds,
    userId: sellerId,
    bundleSummary,
  })

  useEffect(() => {
    const trackingId = getBundleTrackingIdUrlParam(urlQuery)

    if (!trackingId) return

    setBundleTrackingId(trackingId)

    const url = removeBundleTrackingIdUrlParam(relativeUrl, urlQuery)

    replaceHistoryState(url)
  }, [relativeUrl, replaceHistoryState, urlQuery])

  useEffect(() => {
    if (!bundleItems.length) return
    if (isSelectedItemsUpdatedFromUrl) return

    const filteredSelectedItems = bundleItems.filter(({ id }) => preselectedItemIds.includes(id))

    setSelectedItems(filteredSelectedItems)
    setIsSelectedItemsUpdatedFromUrl(true)
  }, [bundleItems, isSelectedItemsUpdatedFromUrl, preselectedItemIds, selectedItems, transactionId])

  const onSetSelectedItems = (items: Array<ProductItemModel>) => {
    setSelectedItems(items)

    const itemIds = items.map(({ id }) => id)

    const updatedUrl = urlWithParams(CREATE_BUNDLE_URL(sellerId), {
      ...searchParams,
      item_ids: itemIds,
    })

    replaceHistoryState(updatedUrl)
  }

  const onLoadItems = () => {
    if (endReached) return

    fetchNextPage()
  }

  const handleItemVisibility: React.ComponentProps<
    typeof ItemsMultiselect
  >['onItemVisible'] = args => {
    const seenItems: Array<number> = []

    const { item, index } = args

    if (seenItems.includes(item.data.id)) return

    if (
      !getIsBundlingAllowed({
        item: item.data,
        bundleItems,
        firstSelectedItemId: selectedItems[0]?.id,
      })
    ) {
      const cannotBundleEvent = cannotBundleButtonViewEvent({
        listingId: item.data.id,
        sellerId,
        reasonForNotBundling: ReasonForNotBundling.ShippingSize,
        screen: 'bundling',
        bundleSessionId: bundleTrackingId,
      })
      track(cannotBundleEvent)
    }

    const showItemEvent = impressionEvent({
      id: item.data.id,
      position: index,
      contentType: 'item',
      contentSource: 'bundle',
      itemOwnerId: item.data.user.id,
    })

    track(showItemEvent)
    seenItems.push(item.data.id)
  }

  const handleItemClick = (item: ProductItemModel, position: number, isSelected: boolean) => {
    if (isSelected) {
      setLastSelectedItemId(item.id.toString())
    } else {
      setLastSelectedItemId(null)
    }

    track(
      clickListItemEvent({
        position,
        id: item.id,
        contentType: 'item',
        contentSource: 'bundle',
      }),
    )

    track(
      clickEvent({
        screen: 'bundling',
        target: isSelected ? 'add_item_to_bundle' : 'remove_item_from_bundle',
        targetDetails: JSON.stringify(
          omitBy(
            {
              item_id: item.id,
              bundle_id: bundleTrackingId,
              transaction_id: transactionId,
            },
            isNil,
          ),
        ),
      }),
    )
  }

  const handlePolicyDescriptionClick = () => {
    track(
      clickEvent({
        target: 'bundles_policy_help_centre_link',
      }),
    )
  }

  const handleReviewBundleLoading = (state: boolean) => {
    setIsBundleReviewLoading(state)
  }

  const clearItemLimitationError = () => {
    setItemLimitationError(null)
  }

  const handleItemLimitationError = useCallback(
    (type: BundlingLimitationsErrorType, limit: CurrencyAmountModel | null) => {
      setItemLimitationError({
        limit,
        type,
      })

      track(
        exceedingLimitItemClickEvent({
          screen: 'bundling',
          bundleSessionId: bundleTrackingId ?? '',
          itemId: lastSelectedItemId ?? '',
        }),
      )

      if (lastSelectedItemId) {
        const filteredIds = selectedItems.filter(item => item.id !== Number(lastSelectedItemId))
        setSelectedItems(filteredIds)
        setLastSelectedItemId(null)

        const updatedUrl = urlWithParams(CREATE_BUNDLE_URL(sellerId), {
          ...searchParams,
          item_ids: filteredIds.map(({ id }) => id),
        })
        replaceHistoryState(updatedUrl)
      }
    },
    [
      bundleTrackingId,
      lastSelectedItemId,
      replaceHistoryState,
      searchParams,
      selectedItems,
      sellerId,
      track,
    ],
  )

  const handleItemError = useCallback(
    (error: ResponseError) => {
      const resolvedError = resolveBundleError(error)

      if (!resolvedError) return

      if (resolvedError.type === BundlingLimitationsErrorType.ItemExceedsLimit) {
        handleItemLimitationError(
          BundlingLimitationsErrorType.ItemExceedsLimit,
          resolvedError.limit,
        )
      }
    },
    [handleItemLimitationError],
  )

  useEffect(() => {
    if (bundleError && !itemLimitationError) {
      handleItemError(bundleError)
    }
  }, [bundleError, handleItemError, itemLimitationError])

  const renderPolicyDescription = () => {
    const values = {
      'policy-link': (chunks: Array<ReactNode>) => (
        <a
          key="policy-link"
          href={BUNDLES_POLICY_URL}
          target="_blank"
          onClick={handlePolicyDescriptionClick}
          rel="noreferrer"
        >
          {chunks}
        </a>
      ),
    }

    return (
      <>
        <Spacer />
        <Text as="h4" text={translate('policy_description', values)} type={Text.Type.Caption} />
      </>
    )
  }

  const renderContent = () => {
    return (
      <>
        <Spacer size={Spacer.Size.XLarge} />
        <Cell
          styling={breakpoints.tabletsUp ? Cell.Styling.Tight : Cell.Styling.Narrow}
          title={
            <Text as="h1" text={translate('build_bundle_title')} type={Text.Type.Heading} bold />
          }
          body={
            <>
              {renderPolicyDescription()}
              {isDigitalRepublicLawEnabled && <OurPlatformDisclaimer />}
              {containsOfflineVerificationItem && (
                <>
                  <Spacer size={Spacer.Size.XLarge} />
                  <ItemVerificationBundleInfoBanner itemVerificationFee={offlineVerificationFee} />
                </>
              )}
              {bundleSummary?.containsElectronicsVerificationItem && (
                <>
                  <Spacer size={Spacer.Size.XLarge} />
                  <ElectronicsVerificationBundleInfoBanner
                    electronicsVerificationFee={bundleSummary?.electronicsVerificationFee}
                  />
                </>
              )}
            </>
          }
        />
        <Spacer size={Spacer.Size.Large} />
        <ItemsMultiselect
          items={bundleItems}
          isLoading={isLoading}
          endReached={!!endReached}
          loadItems={onLoadItems}
          selectedItems={selectedItems}
          onItemVisible={handleItemVisibility}
          onItemClick={handleItemClick}
          updateSelectedItems={onSetSelectedItems}
          showFavourite
          showPriceBreakdown={false}
          bundleTrackingId={bundleTrackingId}
          sellerId={sellerId}
          emptyState={
            <EmptyState
              animation={<Animation animationUrl={asset('closet-empty-state.json')} />}
              title={translate('empty_state.multiselect.title')}
              body={translate('empty_state.multiselect.body')}
            />
          }
          disabled={isBundleReviewLoading}
        />
      </>
    )
  }

  const isSellerBusiness = bundleItems[0]?.user.isBusiness || false

  return (
    <div className="u-flexbox u-flex-direction-column u-fill-viewport">
      <div className="action-header">
        <div className="container">
          <BundleHeaderWithNavigation
            pathBack={MEMBER_PROFILE_URL(sellerId)}
            discountHint={bundleSummary?.discountText}
          />
        </div>
      </div>
      <div className="u-flex-grow">
        <div className="container">{renderContent()}</div>
      </div>
      <BundleFooterWrapper
        selectedItems={selectedItems}
        bundleSummary={bundleSummary}
        bundleTrackingId={bundleTrackingId}
        isSellerBusiness={isSellerBusiness}
        homepageSessionId={homepageSessionId}
        sellerId={sellerId}
        isBundleSummaryLoading={isBundleSummaryLoading}
        transactionId={transactionId}
        checkoutId={checkoutId}
        screen="bundling"
        onReviewBundleLoading={state => handleReviewBundleLoading(state)}
        escrowNoteTranslationKey={escrowNoteTranslationKey}
      />
      {!!itemLimitationError && (
        <BundlingLimitationsModal
          isOpen={!!itemLimitationError}
          limit={itemLimitationError.limit}
          type={itemLimitationError.type}
          bundleTrackingId={bundleTrackingId}
          onClose={clearItemLimitationError}
        />
      )}
    </div>
  )
}

export default BundleItems
